import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Users } from "lucide-react";
import { api, ERROR_TEXT, SUCCESS_TEXT } from "@/lib/api";
import { Arrow } from "@/components/hm/Basics";
import { usePlans, euros } from "@/lib/plans";

export const groupSentences = (body, size = 3) => {
  const out = [];
  for (let i = 0; i < body.length; i += size) out.push(body.slice(i, i + size).join(" "));
  return out;
};

export const ReadProgress = () => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const on = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setPct(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0);
    };
    on(); addEventListener("scroll", on, { passive: true });
    return () => removeEventListener("scroll", on);
  }, []);
  return <div className="read-progress" aria-hidden="true"><b style={{ width: `${pct}%` }} /></div>;
};

export const Paywall = ({ m }) => {
  const plans = usePlans();
  const min = plans?.length ? Math.min(...plans.map((p) => p.amount)) : null;
  const sessions = plans?.filter((p) => p.has_sessions).map((p) => euros(p.amount)) || [];
  return (
  <div data-testid="module-paywall">
    <div className="reader-sec">
      <h4>{m.teaser.title}</h4>
      <p>{m.teaser.body.join(" ")}</p>
    </div>
    <div className="blurred reader-sec" aria-hidden="true"><p>{m.teaser.more.join(" ") || m.summary}</p><p>{m.summary}</p></div>
    <div className="paywall" style={{ marginTop: "-3rem", position: "relative" }}>
      <div className="lock"><Lock /></div>
      <h3>Sigue donde lo dejaste</h3>
      <p className="text-muted" style={{ margin: 0 }}>Este módulo está incluido en los planes. Desbloquea el resto del camino{min !== null && ` desde ${euros(min)} al mes`}.</p>
      <ul>{m.premium_count > 0 && <li>{m.premium_count === 1 ? "Un módulo más" : `${m.premium_count} módulos más`}</li>}<li>Todos los recursos guiados</li>{sessions.length > 0 && <li>Sesiones en directo en los planes de {sessions.join(" y ")}</li>}</ul>
      <div style={{ display: "flex", gap: ".6rem", justifyContent: "center", flexWrap: "wrap" }}>
        <Link to="/planes" className="btn btn-primary" data-testid="paywall-plans-button">Ver los planes</Link>
        <Link to="/academia" className="btn btn-ghost" data-testid="paywall-back-button">Volver a Academia</Link>
      </div>
    </div>
  </div>
  );
};

export const ClarityMoment = ({ m, onDone }) => {
  const key = `hm_draft_${m.id}`;
  const [note, setNote] = useState(() => localStorage.getItem(key) || m.note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (note.trim() && note !== (m.note || "")) localStorage.setItem(key, note);
    else localStorage.removeItem(key);
  }, [key, note, m.note]);

  const save = async () => {
    setSaving(true); setError(false);
    try {
      await api.post(`/modules/${m.id}/complete`, { note });
      localStorage.removeItem(key); onDone();
    } catch { setError(true); } finally { setSaving(false); }
  };

  return (
    <section style={{ marginTop: "2.5rem" }} data-testid="clarity-moment">
      <div className="pull" data-testid="clarity-insight">{m.clarity.insight}</div>
      <div className="journal">
        <span className="eyebrow">Tu momento de claridad</span>
        <label htmlFor="clarity-note" style={{ display: "block", margin: ".9rem 0 .4rem", fontWeight: 600, fontSize: 15 }}>{m.clarity.prompt}</label>
        <textarea id="clarity-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
          placeholder="Escribe aquí. Solo lo verás tú." data-testid="clarity-note-input" />
        {error && <p className="text-danger" style={{ fontSize: 14 }} data-testid="clarity-error">{ERROR_TEXT}</p>}
        <div className="journal-f">
          <span className="inline-flex items-center gap-1"><Lock size={12} /> Solo lo ves tú · el borrador se guarda en este dispositivo</span>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving} data-testid="clarity-save-button">
            {saving ? "Guardando..." : "Guardar y seguir"}
          </button>
        </div>
      </div>
    </section>
  );
};

export const DoneCard = ({ m, nextId }) => (
  <section className="done-card hm-fade-up" data-testid="module-done">
    <p className="hm-h2" style={{ fontSize: "1.4rem" }} data-testid="module-success-text">{SUCCESS_TEXT}</p>
    <p className="text-muted" style={{ margin: "1rem 0 0" }}>Tu siguiente paso: {m.next_step}</p>
    <p className="text-muted" style={{ margin: ".8rem 0 0" }}>Te invitamos a la comunidad. Cuenta en una frase qué has entendido hoy.</p>
    <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginTop: "1.6rem" }}>
      <Link to="/comunidad" className="btn btn-primary mag" data-testid="invite-community-button"><Users size={18} /> Compartir mi avance</Link>
      {nextId ? (
        <Link to={`/academia/${nextId}`} className="btn btn-ghost" data-testid="next-module-button">Seguir con el siguiente módulo <Arrow /></Link>
      ) : (
        <Link to="/academia" className="btn btn-ghost" data-testid="back-academia-button">Volver a Academia</Link>
      )}
    </div>
  </section>
);
